/**
 * Key registries of the inspection checklist (picker tool v0, AUT-371 / P-04).
 *
 * Every array in this file is a dictionary of PERSISTED ids: step keys, chip
 * keys and reason keys are written into the step results, section keys and
 * condition values into the public report, fuel / gearbox / drive into the
 * report's own vehicle columns. A report is a document a buyer decides on and
 * may reopen years later, so these follow the `DEFECT_ZONES` rules
 * (`../defect-zone.ts`):
 *
 * 1. **APPEND-ONLY.** A key is never deleted, never renamed, never reordered.
 *    Retiring a step or a chip means leaving it out of a NEW catalogue version
 *    (`CHECKLIST_CATALOGUES`) — the key stays here and keeps its label, so a
 *    report written against the old version still reads. The guard test
 *    (`__tests__/registry.test.ts`) pins the recorded prefix of every list:
 *    appending passes, anything else fails.
 * 2. **The array index is not a rank.** Display order lives in the catalogue.
 * 3. **snake_case ids, human text in labels** (`labels.ru.ts`). A label may be
 *    reworded at any time; a key may not.
 * 4. **Strict write, tolerant read.** The api rejects an unknown key on write
 *    (400 — a newer client talking to an older server must hear about it);
 *    every reader treats a stored key as a plain `string` and falls back to a
 *    generic label (`read.ts`), never a crash.
 */
/** Route sections, in route order. Append-only. */
export const CHECKLIST_SECTIONS = [
    'start',
    'documents',
    'startup',
    'body',
    'powertrain',
    'wheels',
    'interior',
    'warm',
    'diagnostics',
];
/**
 * Every step key any catalogue version has ever used. Append-only. The ROUTE
 * (order, section, capture, chips) lives in the catalogue, not here — v2 may
 * reorder or drop steps without touching this list.
 */
export const CHECKLIST_STEP_KEYS = [
    // start
    'conditions',
    'engine_parking',
    'overview',
    // documents
    'vin_plate',
    'techpassport_keys',
    // startup
    'dashboard',
    'cold_start',
    'exhaust',
    // body
    'body_front',
    'body_left',
    'body_rear',
    'body_right',
    'roof',
    'glass',
    'lights',
    // powertrain
    'engine_bay',
    'trunk',
    'door_openings',
    // wheels
    'wheels_front',
    'wheels_rear',
    'brakes',
    // interior
    'interior_wear',
    'moisture',
    'airbags',
    'electrics',
    'climate',
    // warm
    'leaks_warm',
    'dipstick_cap',
    'engine_running',
    'battery',
    // diagnostics (A5 — optional, never blocks finalize)
    'diagnostics_files',
];
/**
 * GLOBAL issue chips. A chip means the same thing on every step that allows it
 * (`paint_mismatch` on the left side and on the roof is one finding type), so
 * the dictionary is global and each step carries an allow-list. Append-only.
 */
export const CHECKLIST_CHIP_KEYS = [
    // body / paint
    'paint_mismatch',
    'filler',
    'dent',
    'rust',
    'panel_gap',
    'paint_chips',
    'hail_damage',
    'body_repair_marks',
    'paint_overspray',
    // glass / lights
    'glass_crack',
    'glass_chip',
    'glass_replaced',
    'light_fault',
    'lens_fogged',
    'lens_damage',
    // documents (feed the documents block, create no defect)
    'vin_mismatch',
    'vin_tampered',
    'vin_plate_missing',
    'docs_mismatch',
    'docs_duplicate',
    'owners_many',
    'single_key',
    'service_history_gap',
    // startup / engine
    'warning_lights',
    'mileage_suspect',
    'gauge_fault',
    'hard_start',
    'engine_noise',
    'smoke',
    'rough_idle',
    'smoke_blue',
    'smoke_white',
    'smoke_black',
    'exhaust_leak',
    'fresh_leak_spot',
    'oil_leak',
    'coolant_leak',
    'gearbox_leak',
    'wiring_damage',
    'oil_emulsion',
    'oil_low',
    'oil_dirty',
    'gearbox_shift',
    'battery_corrosion',
    'battery_old',
    'battery_weak',
    // trunk
    'trunk_moisture',
    'spare_missing',
    // wheels / brakes
    'tread_low',
    'tire_old',
    'uneven_wear',
    'tire_damage',
    'rim_damage',
    'brake_discs_worn',
    'brake_pads_worn',
    // interior
    'wear_vs_mileage',
    'upholstery_damage',
    'smell',
    'moisture',
    'flood_traces',
    'mold',
    'airbag_light',
    'airbag_tampered',
    'electrics_fault',
    'multimedia_fault',
    'ac_fault',
    'heater_fault',
    // diagnostics
    'diag_errors',
    // universal — always requires a note
    'other',
    // AUT-465 — catalogue v2 splits v1's «Сколы, царапины» (`paint_chips`, kept
    // for v1) into two point types. `paint_chip`, not a bare `chip`: `glass_chip`
    // is a different finding on a different part.
    'paint_chip',
    'scratch',
];
/** Why a step could not be checked («Не удалось»). Append-only. */
export const CHECKLIST_UNABLE_REASONS = [
    'dark',
    'rain',
    'seller_refused',
    'no_key',
    'no_access',
    'other',
];
/** The one answer every step ends with. Append-only. */
export const STEP_OUTCOMES = ['ok', 'issue', 'na', 'unable'];
/** Vehicle facts typed at start. Append-only. */
export const VEHICLE_FUELS = ['petrol', 'diesel', 'hybrid', 'ev'];
export const VEHICLE_GEARBOXES = ['manual', 'automatic', 'robot', 'cvt'];
export const VEHICLE_DRIVES = ['fwd', 'rwd', 'awd'];
/** AUT-456 — body type, OPTIONAL at start (a report may have none). Append-only. */
export const VEHICLE_BODY_TYPES = [
    'sedan',
    'hatchback',
    'wagon',
    'liftback',
    'coupe',
    'convertible',
    'suv',
    'minivan',
    'pickup',
    'van',
];
/**
 * Body colour of a car on sale (AUT-692 R-2; CHECK `car_listings_color_ck`).
 * Labels live in the clients' i18n. Append-only.
 */
export const VEHICLE_COLORS = [
    'white',
    'black',
    'silver',
    'grey',
    'blue',
    'red',
    'green',
    'brown',
    'beige',
    'yellow',
    'orange',
    'purple',
    'other',
];
/** Inspection conditions (step `conditions`). Append-only. */
export const INSPECTION_LIGHTS = ['day', 'dusk', 'dark', 'indoor'];
export const INSPECTION_WEATHERS = ['dry', 'rain', 'snow', 'frost'];
export const INSPECTION_BODY_STATES = ['clean', 'dusty', 'dirty', 'wet'];
/** Tread depth, one bucket per wheel: >5 mm, 3–5 mm, <3 mm. Append-only. */
export const TREAD_BUCKETS = ['over_5mm', 'from_3_to_5mm', 'under_3mm'];
export const WHEEL_POSITIONS = ['front_left', 'front_right', 'rear_left', 'rear_right'];
/** Service history as read from the book / invoices (step `techpassport_keys`). */
export const SERVICE_HISTORY_STATES = ['full', 'partial', 'none'];
/**
 * Why the public report shows a conditions banner. Derived, never typed by the
 * expert (`conditionsCaveats()` in `conditions.ts`). Append-only because the
 * public DTO carries the keys.
 */
export const CONDITIONS_CAVEATS = [
    'light_not_day',
    'weather_not_dry',
    'body_not_clean',
    'engine_warm_on_arrival',
];
/**
 * The expert's verdict on the finish screen: Брать / Брать с торгом / Не брать.
 * Same wire values as the marketplace `buyRecommendation`. There is NO
 * default — the finalize DTO requires an explicit pick (plan rule 21).
 */
export const REPORT_VERDICTS = ['yes', 'with_repairs', 'no'];
/**
 * Car options catalogue (AUT-740): what a car on sale / in a report / in a
 * request is equipped with. The flat list is the STORED value (CHECK on three
 * columns); groups are display only. Labels live in `labels.ru.options.ts`.
 * Append-only; stored order = catalogue order, no duplicates.
 */
export const VEHICLE_OPTIONS = [
    'panoramic_roof',
    'sunroof',
    'led_headlights',
    'xenon_headlights',
    'fog_lights',
    'headlight_washers',
    'rain_sensor',
    'heated_windshield',
    'power_folding_mirrors',
    'air_conditioning',
    'climate_control',
    'cruise_control',
    'adaptive_cruise_control',
    'keyless_entry',
    'push_button_start',
    'remote_start',
    'power_tailgate',
    'heated_steering_wheel',
    'parking_sensors_front',
    'parking_sensors_rear',
    'rear_camera',
    'camera_360',
    'blind_spot_monitor',
    'lane_keeping_assist',
    'emergency_braking',
    'isofix',
    'alarm',
    'navigation',
    'carplay_android_auto',
    'bluetooth',
    'premium_audio',
    'head_up_display',
    'digital_cluster',
    'wireless_charging',
    'leather_seats',
    'heated_front_seats',
    'heated_rear_seats',
    'ventilated_seats',
    'power_seats',
    'seat_memory',
    'third_row_seats',
];
/** Display groups of `VEHICLE_OPTIONS`, in display order. Append-only. */
export const VEHICLE_OPTION_GROUP_KEYS = [
    'view_light',
    'comfort',
    'safety',
    'media',
    'interior',
];
/** Every option sits in exactly one group (pinned by test). */
export const VEHICLE_OPTION_GROUPS = {
    view_light: [
        'panoramic_roof',
        'sunroof',
        'led_headlights',
        'xenon_headlights',
        'fog_lights',
        'headlight_washers',
        'rain_sensor',
        'heated_windshield',
        'power_folding_mirrors',
    ],
    comfort: [
        'air_conditioning',
        'climate_control',
        'cruise_control',
        'adaptive_cruise_control',
        'keyless_entry',
        'push_button_start',
        'remote_start',
        'power_tailgate',
        'heated_steering_wheel',
    ],
    safety: [
        'parking_sensors_front',
        'parking_sensors_rear',
        'rear_camera',
        'camera_360',
        'blind_spot_monitor',
        'lane_keeping_assist',
        'emergency_braking',
        'isofix',
        'alarm',
    ],
    media: [
        'navigation',
        'carplay_android_auto',
        'bluetooth',
        'premium_audio',
        'head_up_display',
        'digital_cluster',
        'wireless_charging',
    ],
    interior: [
        'leather_seats',
        'heated_front_seats',
        'heated_rear_seats',
        'ventilated_seats',
        'power_seats',
        'seat_memory',
        'third_row_seats',
    ],
};
/**
 * Strict write path: catalogue order, no duplicates; `null` when any key is
 * foreign (the caller maps it to its own error code). `[]` is valid.
 */
export function canonicalOptions(input) {
    const wanted = new Set(input);
    const known = new Set(VEHICLE_OPTIONS);
    for (const key of wanted)
        if (!known.has(key))
            return null;
    return VEHICLE_OPTIONS.filter((key) => wanted.has(key));
}
const includes = (list) => (value) => list.includes(value);
/** Narrowing guards for the READ path — never to silently filter a write. */
export const isChecklistStepKey = includes(CHECKLIST_STEP_KEYS);
export const isChecklistChipKey = includes(CHECKLIST_CHIP_KEYS);
export const isChecklistUnableReason = includes(CHECKLIST_UNABLE_REASONS);
export const isChecklistSection = includes(CHECKLIST_SECTIONS);
export const isStepOutcome = includes(STEP_OUTCOMES);
export const isReportVerdict = includes(REPORT_VERDICTS);
/** Motorcycle type (AUT-869). Append-only. */
export const MOTO_TYPES = [
    'sport',
    'naked',
    'touring',
    'sport_touring',
    'cruiser',
    'chopper',
    'adventure',
    'enduro',
    'motocross',
    'classic',
    'scooter',
    'moped',
    'trike',
];
/** Motorcycle final drive (AUT-869). Append-only. */
export const MOTO_FINAL_DRIVES = ['chain', 'belt', 'shaft'];
/**
 * Moto-only options (AUT-869) — kept apart from `VEHICLE_OPTIONS`, which is a
 * stored car value (CHECK on car columns). Labels: `CHECKLIST_LABELS_RU.motoOption`.
 * Append-only.
 */
export const MOTO_OPTIONS = [
    'abs',
    'traction_control',
    'quickshifter',
    'heated_grips',
    'side_cases',
    'top_case',
    'windscreen',
    'centre_stand',
];
/** What the moto editor shows: the moto options plus two shared car keys. */
export const MOTO_OPTION_GROUP = [...MOTO_OPTIONS, 'cruise_control', 'alarm'];
/** Moto has two wheels; `WHEEL_POSITIONS` stays the car list. Append-only. */
export const MOTO_WHEEL_POSITIONS = ['front', 'rear'];
//# sourceMappingURL=keys.js.map